import { useEffect, useCallback, useRef } from 'react';
import { useProxyStore } from '../store/proxy-store';
import { useProjectStore } from '../store/projectStore';
import type {
  HttpRequest,
  InterceptedRequest,
  ProxySettings,
  HttpRequestResponseUpdate,
} from '../store/proxy-store';
import type {
  NetworkEventData,
  HttpResponse,
  RequestData,
  EditedRequestInfo,
} from '../electron/proxy-types';
import { invalidateCacheEntry } from './useRequestBodies';
import { getCurrentHistoryTarget } from '../utils/current-history-target';
import {
  formatHttpRequestRaw,
  formatHttpResponseRaw,
} from '../utils/formatHttpForPanel';
import {
  buildUpstreamProxyFromSettings,
  formatUpstreamProxyForIpc,
  getProxySettingsPortValidationError,
} from '../electron-shared/utils/proxy.util';

const REQUEST_BATCH_DELAY_MS = 50;
const PROXY_STOP_STATUS_POLL_ATTEMPTS = 30;
const PROXY_STOP_STATUS_POLL_MS = 500;

type PendingRequestUpdate =
  | {
      type: 'add';
      request: HttpRequest;
    }
  | {
      type: 'update';
      requestId: string;
      responseData: {
        status: number;
        responseHeaders: Record<string, string | string[]>;
        responseRawHeaders?: string[];
        responseBody: string;
        length?: number;
      };
    };

let pendingRequestBatch: PendingRequestUpdate[] = [];
let batchTimeoutId: ReturnType<typeof setTimeout> | null = null;

// Singleton flag to prevent duplicate network-request listeners
// This is necessary because useElectronProxy is called from multiple components
let networkRequestListenerRegistered = false;

// Note: electronAPI types are defined in preload.ts - this file uses the global interface

// Fallback toaster implementation for terra-proxy
const toasterApi = {
  success: ({
    title,
    description,
  }: {
    title: string;
    description?: string;
  }) => {
    console.log(`✅ ${title}${description ? `: ${description}` : ''}`);
  },
  error: ({ title, description }: { title: string; description?: string }) => {
    console.error(`❌ ${title}${description ? `: ${description}` : ''}`);
  },
  info: ({ title, description }: { title: string; description?: string }) => {
    console.info(`ℹ️ ${title}${description ? `: ${description}` : ''}`);
  },
  warning: ({
    title,
    description,
  }: {
    title: string;
    description?: string;
  }) => {
    console.warn(`⚠️ ${title}${description ? `: ${description}` : ''}`);
  },
};

// Helper functions for URL parsing (exported for UI display components)
export const extractHostFromUrl = (url: string): string => {
  try {
    const urlObj = new URL(url.startsWith('http') ? url : `http://${url}`);

    return urlObj.hostname;
  } catch {
    return url.split('/')[0] || 'unknown';
  }
};

export const extractPathFromUrl = (url: string): string => {
  try {
    const urlObj = new URL(url.startsWith('http') ? url : `http://${url}`);

    return urlObj.pathname;
  } catch {
    const parts = url.split('?')[0].split('/');

    return parts.length > 1 ? '/' + parts.slice(1).join('/') : '/';
  }
};

export const extractQueryFromUrl = (url: string): string => {
  try {
    const urlObj = new URL(url.startsWith('http') ? url : `http://${url}`);

    return urlObj.search.substring(1); // Remove the '?' prefix
  } catch {
    const queryIndex = url.indexOf('?');

    return queryIndex !== -1 ? url.substring(queryIndex + 1) : '';
  }
};

export function useElectronProxy() {
  const settings = useProxyStore((state) => state.settings);
  const interceptRulesHydrationPending = useProxyStore(
    (state) => state.interceptRulesHydrationPending,
  );
  const setProxyRunning = useProxyStore((state) => state.setProxyRunning);
  const setProxyStopping = useProxyStore((state) => state.setProxyStopping);
  const addRequest = useProxyStore((state) => state.addRequest);
  const addRequests = useProxyStore((state) => state.addRequests);
  const updateRequestWithResponse = useProxyStore(
    (state) => state.updateRequestWithResponse,
  );
  const updateRequestsWithResponse = useProxyStore(
    (state) => state.updateRequestsWithResponse,
  );
  const setRequestProxyError = useProxyStore(
    (state) => state.setRequestProxyError,
  );
  const addRequestProxyWarning = useProxyStore(
    (state) => state.addRequestProxyWarning,
  );
  const addToInterceptQueue = useProxyStore(
    (state) => state.addToInterceptQueue,
  );
  const clearRequests = useProxyStore((state) => state.clearRequests);

  // Signal renderer readiness to main process
  useEffect(() => {
    if (typeof window !== 'undefined' && window.electronAPI) {
      window.electronAPI.signalReady().catch((err) => {
        console.error('Failed to signal renderer ready:', err);
      });
    }
  }, []);

  // Store callbacks in refs to prevent re-creating event listeners
  const addRequestRef = useRef(addRequest);
  const addRequestsRef = useRef(addRequests);
  const addToInterceptQueueRef = useRef(addToInterceptQueue);
  const updateRequestWithResponseRef = useRef(updateRequestWithResponse);
  const updateRequestsWithResponseRef = useRef(updateRequestsWithResponse);

  // Update refs when callbacks change
  useEffect(() => {
    addRequestRef.current = addRequest;
    addRequestsRef.current = addRequests;
    addToInterceptQueueRef.current = addToInterceptQueue;
    updateRequestWithResponseRef.current = updateRequestWithResponse;
    updateRequestsWithResponseRef.current = updateRequestsWithResponse;
  });

  const flushRequestBatch = useCallback(() => {
    if (pendingRequestBatch.length === 0) return;

    const batch = pendingRequestBatch;

    pendingRequestBatch = [];
    batchTimeoutId = null;

    // Process all adds first, then all updates
    const adds = batch
      .filter(
        (u): u is Extract<PendingRequestUpdate, { type: 'add' }> =>
          u.type === 'add',
      )
      .map((u) => u.request);

    const updates = batch
      .filter(
        (u): u is Extract<PendingRequestUpdate, { type: 'update' }> =>
          u.type === 'update',
      )
      .map((u) => ({
        requestId: u.requestId,
        responseData: u.responseData,
      }));

    // Add all requests in one batch
    if (adds.length > 0) {
      addRequestsRef.current(adds);
    }

    // Apply all response updates in one batch
    if (updates.length > 0) {
      updateRequestsWithResponseRef.current(updates);
    }
  }, []);

  const batchedAddRequest = useCallback(
    (request: HttpRequest) => {
      pendingRequestBatch.push({ type: 'add', request });

      if (!batchTimeoutId) {
        batchTimeoutId = setTimeout(flushRequestBatch, REQUEST_BATCH_DELAY_MS);
      }
    },
    [flushRequestBatch],
  );

  const batchedUpdateRequest = useCallback(
    (requestId: string, responseData: HttpRequestResponseUpdate) => {
      pendingRequestBatch.push({ type: 'update', requestId, responseData });

      if (!batchTimeoutId) {
        batchTimeoutId = setTimeout(flushRequestBatch, REQUEST_BATCH_DELAY_MS);
      }
    },
    [flushRequestBatch],
  );

  // Handle real-time network requests from Electron
  useEffect(() => {
    if (typeof window === 'undefined' || !window.electronAPI) return;

    const handleNetworkRequest = (data: NetworkEventData) => {
      switch (data.type) {
        case 'request': {
          const requestData = data.data as RequestData;
          const httpRequest: HttpRequest = {
            id: requestData.id,
            sequentialId: requestData.sequentialId,
            method: requestData.method,
            url: requestData.url,
            host: requestData.host,
            headers: requestData.headers || {},
            rawHeaders: requestData.rawHeaders,
            body: requestData.body || '',
            bodyRef: requestData.bodyRef,
            bodySize: requestData.bodySize,
            bodyStorageType: requestData.bodyStorageType,
            bodyContentType: requestData.bodyContentType,
            bodyPreview: requestData.bodyPreview,
            wasIntercepted: requestData.wasIntercepted,
            wasProxyModified: requestData.wasProxyModified,
            originalRequest: requestData.originalRequest,
            timestamp:
              typeof requestData.timestamp === 'number'
                ? new Date(requestData.timestamp)
                : new Date(),
            source: requestData.source || 'unknown',
            httpVersion: requestData.httpVersion || '1.1',
            status: undefined,
            length: requestData.body ? requestData.body.length : 0,
          };

          batchedAddRequest(httpRequest);
          break;
        }

        case 'response':
          if (
            data.data &&
            typeof (data.data as Record<string, unknown>).requestId === 'string'
          ) {
            const responseData = data.data as HttpResponse;
            const responseUpdate = {
              status: responseData.statusCode,
              responseStatusMessage: responseData.statusMessage,
              responseHeaders: responseData.headers,
              responseRawHeaders: responseData.rawHeaders,
              responseBody: responseData.body,
              responseBodyRef: responseData.bodyRef,
              responseBodyStorageType: responseData.bodyStorageType,
              responseBodyContentType: responseData.bodyContentType,
              responseBodyPreview: responseData.bodyPreview,
              responseHttpVersion: responseData.httpVersion,
              ...(responseData.wasProxyModified
                ? { wasProxyModified: true }
                : {}),
              length:
                responseData.size ??
                (responseData.body ? responseData.body.length : 0),
            };

            batchedUpdateRequest(responseData.requestId, responseUpdate);
          } else {
            console.warn('Response data missing requestId:', data.data);
          }

          break;

        case 'request-error': {
          const errorData = data.data as { requestId: string; error: string };

          if (
            typeof errorData.requestId === 'string' &&
            typeof errorData.error === 'string'
          ) {
            setRequestProxyError(errorData.requestId, errorData.error);
          }

          break;
        }

        case 'request-warning': {
          const warningData = data.data as {
            requestId: string;
            warning: string;
          };

          if (
            typeof warningData.requestId === 'string' &&
            typeof warningData.warning === 'string'
          ) {
            addRequestProxyWarning(warningData.requestId, warningData.warning);
          }

          break;
        }

        case 'intercept': {
          const requestData = data.data as RequestData;
          const httpRequest: HttpRequest = {
            id: requestData.id,
            sequentialId: requestData.sequentialId,
            method: requestData.method,
            url: requestData.url,
            host: requestData.host,
            headers: requestData.headers || {},
            rawHeaders: requestData.rawHeaders,
            body: requestData.body || '',
            bodyRef: requestData.bodyRef,
            bodySize: requestData.bodySize,
            bodyStorageType: requestData.bodyStorageType,
            bodyContentType: requestData.bodyContentType,
            bodyPreview: requestData.bodyPreview,
            wasIntercepted: requestData.wasIntercepted,
            timestamp:
              typeof requestData.timestamp === 'number'
                ? new Date(requestData.timestamp)
                : new Date(),
            source: requestData.source || 'unknown',
            httpVersion: requestData.httpVersion || '1.1',
            status: undefined,
            length: requestData.body ? requestData.body.length : 0,
          };
          const formattedRequestText = formatHttpRequestRaw({
            method: httpRequest.method,
            url: httpRequest.url,
            headers: httpRequest.headers,
            rawHeaders: httpRequest.rawHeaders,
            body: httpRequest.body,
            httpVersion: httpRequest.httpVersion,
          });
          const interceptedRequest: InterceptedRequest = {
            id: requestData.id,
            type: 'request',
            request: httpRequest, // Use properly converted HttpRequest
            requestText: formattedRequestText,
            originalRequestText: formattedRequestText, // Store original for edit detection
            timestamp:
              typeof requestData.timestamp === 'number'
                ? new Date(requestData.timestamp)
                : new Date(), // Fallback to current time
          };

          // Add immediately to avoid race condition with responses
          addRequestRef.current(httpRequest);
          addToInterceptQueueRef.current(interceptedRequest);
          break;
        }

        case 'intercept-response': {
          const responseData = data.data as HttpResponse & {
            requestId: string;
          };
          const requestId = responseData.requestId;

          // Check if the request is already in the queue waiting for response
          const currentInterceptQueue = useProxyStore.getState().interceptQueue;

          // Get fresh requests from store instead of using stale closure
          const currentRequests = useProxyStore.getState().requests;
          let originalRequest = currentRequests[requestId];

          // If not found, check in the intercept queue
          if (!originalRequest) {
            const interceptedReq = currentInterceptQueue.find(
              (item) => item.request.id === requestId,
            );

            if (interceptedReq) {
              originalRequest = interceptedReq.request;
            }
          }

          if (!originalRequest) {
            console.warn(
              '[useElectronProxy] Intercepted response but request not found:',
              {
                requestId,
                availableRequestIds: Object.keys(currentRequests).slice(0, 10),
                interceptQueueIds: useProxyStore
                  .getState()
                  .interceptQueue.map((item) => item.request.id)
                  .slice(0, 10),
              },
            );
            break;
          }

          const formattedResponseText = formatHttpResponseRaw({
            status: responseData.statusCode,
            statusMessage: responseData.statusMessage || '',
            headers: responseData.headers,
            body: responseData.body || '',
            httpVersion: responseData.httpVersion || '1.1',
          });

          const interceptedResponse: InterceptedRequest = {
            id: responseData.id,
            type: 'response',
            request: {
              ...originalRequest,
              status: responseData.statusCode,
              responseHeaders: responseData.headers,
              responseBody: responseData.body,
            },
            requestText: '', // Not needed for responses
            responseText: formattedResponseText,
            originalResponseText: formattedResponseText, // Track original to detect modifications
            timestamp: new Date(),
          };

          console.log(
            '[useElectronProxy] Adding intercepted response to queue:',
            {
              id: interceptedResponse.id,
              type: interceptedResponse.type,
              requestId: interceptedResponse.request.id,
            },
          );

          // Check if the request is already in the queue waiting for response
          const existingRequest = currentInterceptQueue.find(
            (item) =>
              item.request.id === requestId && item.status === 'resumed',
          );

          console.log('[useElectronProxy] Checking for existing request:', {
            requestId,
            queueLength: currentInterceptQueue.length,
            foundExisting: !!existingRequest,
            existingId: existingRequest?.id,
            existingStatus: existingRequest?.status,
          });

          if (existingRequest) {
            console.log(
              '[useElectronProxy] UPDATING existing request to response',
              { existingId: existingRequest.id },
            );
            // Update the existing request to become a response instead of adding new
            useProxyStore
              .getState()
              .updateInterceptedItemToResponse(
                existingRequest.id,
                interceptedResponse,
              );
          } else {
            console.log(
              '[useElectronProxy] ADDING new response item (no existing request found)',
              { responseId: interceptedResponse.id },
            );
            // Only add as new item if there's no existing request waiting
            addToInterceptQueueRef.current(interceptedResponse);
          }

          break;
        }

        case 'match-replace-hit': {
          const hitData = data.data as { ruleIds: string[] };

          for (const ruleId of hitData.ruleIds) {
            useProxyStore.getState().incrementRuleMatchCount(ruleId);
          }

          break;
        }

        case 'request-edited': {
          const editedInfo = data.data as EditedRequestInfo;
          const currentRequests = useProxyStore.getState().requests;
          const existingRequest = currentRequests[editedInfo.requestId];

          if (existingRequest) {
            const updatedRequest: HttpRequest = {
              ...existingRequest,
              method: editedInfo.editedRequest.method,
              url: editedInfo.editedRequest.url,
              headers: editedInfo.editedRequest.headers,
              body: editedInfo.editedRequest.body,
              bodySize: editedInfo.editedRequest.body?.length || 0,
              wasEdited: true,
              originalRequest: {
                method: editedInfo.originalRequest.method,
                url: editedInfo.originalRequest.url,
                headers: editedInfo.originalRequest.headers,
              },
            };

            // Body is stored in SQLite by the main process.
            // Invalidate cache so the renderer re-fetches from SQLite.
            invalidateCacheEntry(editedInfo.requestId);

            addRequestRef.current(updatedRequest);
          }

          break;
        }

        default:
          console.warn('Unknown network request type:', data.type);
      }
    };

    // Singleton pattern: Only register the network-request listener once
    // Menu listeners are handled in AppLayout.tsx, no need to duplicate here
    if (networkRequestListenerRegistered) {
      return; // Already registered, skip
    }

    // Create wrapper for network request handler to match event listener signature
    const handleNetworkRequestWrapper = (...args: unknown[]) => {
      const data = args[0] as NetworkEventData;

      handleNetworkRequest(data);
    };

    // Set up the network-request listener (only once)
    window.electronAPI.on('network-request', handleNetworkRequestWrapper);
    networkRequestListenerRegistered = true;

    const handleHistoryMigrationComplete = () => {
      useProxyStore.setState((state) => ({
        historyVersion: state.historyVersion + 1,
        historyStructureVersion: state.historyStructureVersion + 1,
      }));
    };

    window.electronAPI.on(
      'history-db:migration-complete',
      handleHistoryMigrationComplete,
    );

    return () => {
      if (
        typeof window !== 'undefined' &&
        window.electronAPI &&
        window.electronAPI.removeListener
      ) {
        window.electronAPI.removeListener(
          'network-request',
          handleNetworkRequestWrapper,
        );
        window.electronAPI.removeListener(
          'history-db:migration-complete',
          handleHistoryMigrationComplete,
        );
        networkRequestListenerRegistered = false;
      }
    };
  }, [
    batchedAddRequest,
    batchedUpdateRequest,
    setRequestProxyError,
    addRequestProxyWarning,
  ]);

  // Initialize proxy status on mount
  useEffect(() => {
    if (typeof window === 'undefined' || !window.electronAPI) return;

    const initializeProxyStatus = async () => {
      try {
        // Add a small delay to ensure Electron API is fully ready
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Verify API is still available after delay
        if (!window.electronAPI) {
          console.warn('Electron API not available after initialization delay');

          return;
        }

        const status = await window.electronAPI.proxy.getStatus();

        setProxyRunning(status.isRunning);

        // Load existing requests if any
        const requests = await window.electronAPI.requests.get();

        requests.forEach((req) => {
          const httpRequest: HttpRequest = {
            id: req.id,
            sequentialId: req.sequentialId,
            method: req.method,
            url: req.url,
            host: req.host,
            headers: req.headers || {},
            rawHeaders: req.rawHeaders,
            body: req.body || '',
            bodyRef: req.bodyRef,
            bodySize: req.bodySize,
            bodyStorageType: req.bodyStorageType,
            bodyContentType: req.bodyContentType,
            bodyPreview: req.bodyPreview,
            timestamp:
              typeof req.timestamp === 'number'
                ? new Date(req.timestamp)
                : new Date(),
            source: req.source || 'unknown',
            httpVersion: req.httpVersion || '1.1',
            status: undefined,
            length: req.body ? req.body.length : 0,
          };

          addRequest(httpRequest);
        });
      } catch (error) {
        console.error('Failed to initialize proxy status:', error);
      }
    };

    initializeProxyStatus();
  }, [setProxyRunning, addRequest]);

  // Watch for application changes and update proxy context
  useEffect(() => {
    if (typeof window === 'undefined' || !window.electronAPI) {
      return;
    }

    let previousApp = useProjectStore.getState().selectedTarget;

    const unsubscribe = useProjectStore.subscribe(async (state) => {
      const selectedApp = state.selectedTarget;

      // Only update if app actually changed
      if (selectedApp === previousApp) {
        return;
      }

      previousApp = selectedApp;

      // Only update if proxy is running
      const { isProxyRunning } = useProxyStore.getState();

      if (!isProxyRunning) {
        return;
      }

      try {
        const projectState = useProjectStore.getState();
        const appId = projectState.targetId || null;

        await window.electronAPI.proxy.setApplicationContext(
          selectedApp?.url || null,
          selectedApp?.domains || [],
          appId,
          projectState.targetType,
        );
      } catch (error) {
        console.error('Failed to update application context:', error);
      }
    });

    return () => unsubscribe();
  }, []);

  const startProxy = useCallback(
    async (customSettings?: Partial<ProxySettings>) => {
      if (typeof window === 'undefined' || !window.electronAPI) {
        toasterApi.error({
          title: 'Electron API Not Available',
          description: 'This app must be run in Electron environment',
        });

        return { success: false, error: 'Electron API not available' };
      }

      try {
        const effectiveSettings = {
          ...settings,
          ...customSettings,
        };

        const portValidationError =
          getProxySettingsPortValidationError(effectiveSettings);

        if (portValidationError) {
          setProxyRunning(false);
          toasterApi.error({
            title: 'Invalid proxy port',
            description: portValidationError,
          });

          return { success: false, error: portValidationError };
        }

        // Convert UI intercept rules to proxy server filter rules
        const filterRules = effectiveSettings.interceptRules
          .filter((rule) => rule.enabled) // Only include enabled rules
          .map((rule) => ({
            type: 'include' as const, // Include matching requests for interception
            pattern: rule.urlPattern,
            isRegex:
              rule.urlPattern.includes('*') || rule.urlPattern.includes('?'), // Simple wildcard detection
          }));

        const proxyConfig = {
          port: effectiveSettings.port,
          host: effectiveSettings.host,
          interceptEnabled: interceptRulesHydrationPending
            ? false
            : effectiveSettings.interceptEnabled,
          interceptResponsesEnabled:
            effectiveSettings.interceptResponsesEnabled,
          filterRules: filterRules,
          enableHttpsInterception: effectiveSettings.enableHttps,
          httpsPort: effectiveSettings.httpsPort,
          showOnlyInScopeRequests: effectiveSettings.showOnlyInScopeRequests,
          matchReplaceEnabled: true,
          matchReplaceRules: (effectiveSettings.matchReplaceRules || [])
            .filter((rule) => rule.enabled)
            .map((rule) => ({
              id: rule.id,
              name: rule.name,
              enabled: rule.enabled,
              matchType: rule.matchType,
              matchLocation: rule.matchLocation,
              matchPattern: rule.matchPattern,
              isRegex: rule.isRegex,
              caseSensitive: rule.caseSensitive,
              headerName: rule.headerName,
              replaceWith: rule.replaceWith,
              replaceType: rule.replaceType,
              applyTo: rule.applyTo,
              conditions: rule.conditions,
            })),
          // Add other middleware configurations
          headerModification: {
            enabled: effectiveSettings.modifyHeaders || false,
            customHeaders: effectiveSettings.customHeaders || {},
            removeHeaders: effectiveSettings.removeHeaders || [],
          },
          hostFiltering: {
            allowedHosts: effectiveSettings.allowedHosts || [],
            blockedHosts: effectiveSettings.blockedHosts || [],
          },
          upstreamProxy: formatUpstreamProxyForIpc({
            upstreamEnabled: effectiveSettings.upstreamEnabled,
            upstreamHost: effectiveSettings.upstreamHost,
            upstreamPort: effectiveSettings.upstreamPort,
          }),
          upstreamAuth:
            effectiveSettings.upstreamEnabled &&
            effectiveSettings.upstreamAuth?.username
              ? {
                  username: effectiveSettings.upstreamAuth.username,
                  password: effectiveSettings.upstreamAuth.password ?? '',
                }
              : undefined,
          validateUpstreamTls: effectiveSettings.validateUpstreamTls,
        };

        const result = await window.electronAPI.proxy.start(proxyConfig);

        if (result.success) {
          setProxyRunning(true);

          // Set application context for domain filtering
          const selectedApp = useProjectStore.getState().selectedTarget;

          if (selectedApp) {
            try {
              const projectState = useProjectStore.getState();
              const appId = projectState.targetId || null;

              await window.electronAPI.proxy.setApplicationContext(
                selectedApp.url || null,
                selectedApp.domains || [],
                appId,
                projectState.targetType,
              );
            } catch (error) {
              console.error('Failed to set application context:', error);
            }
          }

          toasterApi.success({
            title: 'Proxy Started',
            description: `Proxy server is running on port ${result.port} with ${filterRules.length} active rule(s)`,
          });
        } else {
          setProxyRunning(false);
          const failureMessage =
            result.error || result.message || 'Unknown error occurred';

          toasterApi.error({
            title: 'Failed to Start Proxy',
            description: failureMessage,
          });
        }

        return result;
      } catch (error) {
        console.error('Error starting proxy:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';

        toasterApi.error({
          title: 'Proxy Start Error',
          description: errorMessage,
        });

        return { success: false, error: errorMessage };
      }
    },
    [settings, interceptRulesHydrationPending, setProxyRunning],
  );

  const stopProxy = useCallback(async () => {
    if (typeof window === 'undefined' || !window.electronAPI) {
      toasterApi.error({
        title: 'Electron API Not Available',
        description: 'This app must be run in Electron environment',
      });

      return { success: false, error: 'Electron API not available' };
    }

    if (useProxyStore.getState().isProxyStopping) {
      return { success: true, message: 'Proxy stop already in progress' };
    }

    setProxyStopping(true);

    const markProxyStopped = async () => {
      setProxyRunning(false);

      try {
        await window.electronAPI.proxy.setApplicationContext(
          null,
          [],
          null,
          null,
        );
      } catch (error) {
        console.error('Failed to clear application context:', error);
      }

      toasterApi.success({
        title: 'Proxy Stopped',
        description: 'Proxy server has been stopped',
      });
    };

    try {
      const result = await window.electronAPI.proxy.stop();

      if (result.success) {
        await markProxyStopped();
      } else {
        const failureMessage =
          result.error || result.message || 'Unknown error occurred';
        const timedOut =
          /timed out/i.test(failureMessage) ||
          /still in progress/i.test(failureMessage);

        if (timedOut) {
          for (
            let attempt = 0;
            attempt < PROXY_STOP_STATUS_POLL_ATTEMPTS;
            attempt += 1
          ) {
            await new Promise((resolve) =>
              setTimeout(resolve, PROXY_STOP_STATUS_POLL_MS),
            );
            const status = await window.electronAPI.proxy.getStatus();

            if (!status.isRunning) {
              await markProxyStopped();

              return {
                success: true,
                message: 'Proxy stopped successfully',
              };
            }
          }
        }

        toasterApi.error({
          title: 'Failed to Stop Proxy',
          description: failureMessage,
        });
      }

      return result;
    } catch (error) {
      console.error('Error stopping proxy:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';

      toasterApi.error({
        title: 'Proxy Stop Error',
        description: errorMessage,
      });

      return { success: false, error: errorMessage };
    } finally {
      setProxyStopping(false);
    }
  }, [setProxyRunning, setProxyStopping]);

  const clearHistory = useCallback(async () => {
    if (typeof window === 'undefined' || !window.electronAPI)
      return { success: false, error: 'Electron API not available' };

    try {
      const result = await window.electronAPI.proxy.clearRequests(
        getCurrentHistoryTarget(),
      );

      if (!result.success) {
        throw new Error(result.error || 'Failed to clear request history');
      }

      clearRequests();

      toasterApi.success({
        title: 'History Cleared',
        description: 'Request history for this application has been cleared',
      });

      return result;
    } catch (error) {
      console.error('Error clearing history:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';

      toasterApi.error({
        title: 'Clear History Error',
        description: errorMessage,
      });

      return { success: false, error: errorMessage };
    }
  }, [clearRequests]);

  const getProxyStatus = useCallback(async () => {
    if (typeof window === 'undefined' || !window.electronAPI) {
      console.warn('Electron API not available for status check');

      return {
        success: false,
        data: { isRunning: false, port: null, httpsPort: null },
      };
    }

    try {
      const result = await window.electronAPI.proxy.getStatus();

      return {
        success: true,
        data: {
          isRunning: result.isRunning,
          port: result.port || null,
          httpsPort: result.httpsPort || null,
        },
      };
    } catch (error) {
      console.error('Error getting proxy status:', error);

      return {
        success: false,
        data: { isRunning: false, port: null, httpsPort: null },
      };
    }
  }, []);

  const updateProxyConfig = useCallback(
    async (newSettings?: Partial<ProxySettings>) => {
      if (typeof window === 'undefined' || !window.electronAPI) {
        console.warn('Electron API not available, cannot update proxy config');

        return { success: false, error: 'Electron API not available' };
      }

      try {
        // Check if proxy is running before attempting to update config
        const status = await getProxyStatus();

        if (!status.success || !status.data || !status.data.isRunning) {
          console.warn('Proxy is not running, skipping config update', {
            status,
          });

          return { success: true, message: 'Proxy not running' };
        }

        const currentSettings = {
          ...settings,
          ...newSettings,
        };

        const portValidationError =
          getProxySettingsPortValidationError(currentSettings);

        if (portValidationError) {
          toasterApi.error({
            title: 'Invalid proxy port',
            description: portValidationError,
          });

          return { success: false, error: portValidationError };
        }

        // Prepare updated configuration
        const filterRules = (currentSettings.interceptRules || [])
          .filter((rule) => rule.enabled)
          .map((rule) => ({
            type: 'include' as const,
            pattern: rule.urlPattern,
            isRegex: rule.urlPattern.includes('*'), // Simple wildcard detection
          }));

        const updatedConfig = {
          port: currentSettings.port,
          host: currentSettings.host,
          interceptEnabled: interceptRulesHydrationPending
            ? false
            : currentSettings.interceptEnabled,
          interceptResponsesEnabled: currentSettings.interceptResponsesEnabled,
          showOnlyInScopeRequests: currentSettings.showOnlyInScopeRequests,
          filterRules: filterRules,
          enableHttpsInterception: currentSettings.enableHttps,
          httpsPort: currentSettings.httpsPort,
          matchReplaceRules: {
            enabled: true,
            rules: (currentSettings.matchReplaceRules || []).map((rule) => ({
              id: rule.id,
              name: rule.name,
              enabled: rule.enabled,
              matchType: rule.matchType,
              matchLocation: rule.matchLocation,
              matchPattern: rule.matchPattern,
              isRegex: rule.isRegex,
              caseSensitive: rule.caseSensitive,
              headerName: rule.headerName,
              replaceWith: rule.replaceWith,
              replaceType: rule.replaceType,
              applyTo: rule.applyTo,
              conditions: rule.conditions,
            })),
          },
          headerModification: {
            enabled: currentSettings.modifyHeaders || false,
            customHeaders: currentSettings.customHeaders || {},
            removeHeaders: currentSettings.removeHeaders || [],
          },
          hostFiltering: {
            allowedHosts: currentSettings.allowedHosts || [],
            blockedHosts: currentSettings.blockedHosts || [],
          },
          upstreamProxy: buildUpstreamProxyFromSettings({
            upstreamEnabled: !!currentSettings.upstreamEnabled,
            upstreamHost: currentSettings.upstreamHost,
            upstreamPort: currentSettings.upstreamPort,
            upstreamAuth: currentSettings.upstreamAuth,
          }),
          validateUpstreamTls: currentSettings.validateUpstreamTls,
        };

        const result =
          await window.electronAPI.proxy.updateConfig(updatedConfig);

        if (result.success) {
          // Don't show toast for automatic updates to avoid noise
        } else {
          // Fallback to restart if hot update failed
          await stopProxy();
          const restartResult = await startProxy(currentSettings);

          if (restartResult.success) {
            toasterApi.info({
              title: 'Configuration Updated',
              description: 'Proxy restarted to apply configuration changes',
            });
          } else {
            setProxyRunning(false);
            toasterApi.error({
              title: 'Config Update Failed',
              description:
                restartResult.error ||
                result.error ||
                'Failed to update proxy configuration',
            });
          }

          return restartResult;
        }

        return result;
      } catch (error) {
        console.error('Error updating proxy config:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';

        toasterApi.error({
          title: 'Config Update Error',
          description: errorMessage,
        });

        return { success: false, error: errorMessage };
      }
    },
    [
      settings,
      interceptRulesHydrationPending,
      getProxyStatus,
      startProxy,
      stopProxy,
      setProxyRunning,
    ],
  );

  return {
    startProxy,
    stopProxy,
    clearHistory,
    getProxyStatus,
    updateProxyConfig,
    isElectronAvailable: typeof window !== 'undefined' && !!window.electronAPI,
  };
}
